import { useCallback, useEffect, useState } from 'react';
import { isAccessTokenValid, redeemAccessCode } from '@/lib/site-access-client';
import { clearAccessToken, readAccessToken, writeAccessToken } from '@/lib/site-access-storage';

export type SiteAccessStatus = 'checking' | 'locked' | 'unlocked';

/** `rejected`: el código no es el correcto. `unavailable`: no se pudo consultar. */
export type SiteAccessError = 'rejected' | 'unavailable';

export type SiteAccessOutcome = 'granted' | SiteAccessError;

interface SiteAccess {
  status: SiteAccessStatus;
  error: SiteAccessError | null;
  submitting: boolean;
  /** Devuelve el resultado además de asentarlo, para que la pantalla pueda
   *  reaccionar al intento puntual y no a una transición de estado. */
  submit: (code: string) => Promise<SiteAccessOutcome>;
}

/**
 * Estado de la puerta del sitio. Toda decisión de apertura la toma el backend:
 * acá sólo se traduce su respuesta a lo que la pantalla tiene que mostrar.
 */
export function useSiteAccess(): SiteAccess {
  const [status, setStatus] = useState<SiteAccessStatus>('checking');
  const [error, setError] = useState<SiteAccessError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Revalidación al cargar: un token guardado no abre nada por sí solo.
  useEffect(() => {
    let active = true;
    const token = readAccessToken();

    if (!token) {
      setStatus('locked');
      return;
    }

    void isAccessTokenValid(token).then((valid) => {
      if (!active) return;
      if (!valid) clearAccessToken();
      setStatus(valid ? 'unlocked' : 'locked');
    });

    return () => {
      active = false;
    };
  }, []);

  const submit = useCallback(async (code: string): Promise<SiteAccessOutcome> => {
    setSubmitting(true);
    setError(null);

    const attempt = await redeemAccessCode(code);
    setSubmitting(false);

    if (attempt.status !== 'granted') {
      setError(attempt.status);
      return attempt.status;
    }

    writeAccessToken(attempt.token);
    setStatus('unlocked');
    return 'granted';
  }, []);

  return { status, error, submitting, submit };
}
